import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { PortableText } from '@portabletext/react';
import { useParams } from 'react-router-dom';
import { BlogPostView } from 'alakai-aloha-shared/public-ui';
import PlanningPhasePromo from '../../components/shared/PlanningPhasePromo';
import { islandScopedDocumentFilter, sanityFetch } from '../../lib/sanity';
import { urlFor } from '../../lib/sanityImage';
import { buildRuntimeCanonicalUrl } from '../../lib/siteUrls';
import { getRuntimeSiteContext } from '../../lib/siteContext';

function mapCardPost(post) {
  return {
    id: post._id,
    title: post.title,
    slug: post.slug?.current || post.slug,
    excerpt: post.excerpt,
    imageUrl: post.mainImage?.asset
      ? urlFor(post.mainImage).width(400).height(300).auto('format').quality(70).url()
      : undefined,
    imageAlt: post.mainImage?.alt || post.title,
  };
}

const portableTextComponents = {
  types: {
    image: ({ value }) => {
      if (!value?.asset) return null;
      const alt = value.alt || value.caption || 'Post image';
      return (
        <figure className="my-6">
          <img
            src={urlFor(value).width(1200).auto('format').quality(70).url()}
            alt={alt}
            className="rounded block mx-auto"
            loading="lazy"
          />
          {value.caption && (
            <figcaption className="mt-2 text-sm opacity-80 text-center">
              {value.caption}
            </figcaption>
          )}
        </figure>
      );
    },
    table: ({ value }) => {
      if (!value?.rows?.length) return null;
      const [headerRow, ...bodyRows] = value.rows;
      return (
        <div className="my-6 overflow-x-auto">
          <table className="min-w-full border-collapse border border-white/15 text-sm text-white">
            {headerRow && (
              <thead className="bg-white/10">
                <tr>
                  {headerRow.cells?.map((cell, index) => (
                    <th key={index} className="border border-white/15 px-4 py-2 text-left font-semibold">
                      {cell}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {bodyRows.map((row, rowIndex) => (
                <tr key={row._key || rowIndex} className={rowIndex % 2 === 0 ? 'bg-white/5' : 'bg-white/10'}>
                  {row.cells?.map((cell, cellIndex) => (
                    <td key={cellIndex} className="border border-white/15 px-4 py-2">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    },
  },
  marks: {
    link: ({ value, children }) => (
      <a
        href={value.href}
        className="underline hover:opacity-80"
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ),
  },
  block: {
    h1: ({ children }) => <h1 className="text-4xl font-extrabold tracking-tight mt-12 mb-6">{children}</h1>,
    h2: ({ children }) => <h2 className="text-3xl font-bold mt-10 mb-4 border-b border-gray-200 pb-1">{children}</h2>,
    h3: ({ children }) => <h3 className="text-xl font-semibold mt-6 mb-2">{children}</h3>,
    h4: ({ children }) => <h4 className="text-lg font-medium italic mt-4 mb-1">{children}</h4>,
    conclusion: ({ children }) => (
      <h2 className="text-3xl font-bold mt-12 mb-6 pt-6 pb-4 border-t border-b border-gray-300 tracking-wide">
        {children}
      </h2>
    ),
    normal: ({ children }) => <p className="mb-4">{children}</p>,
    blockquote: ({ children }) => (
      <blockquote className="italic border-l-4 border-green-300 pl-4 mt-8">
        {children}
      </blockquote>
    ),
  },
  list: {
    bullet: ({ children }) => <ul className="list-disc ml-6 mb-4">{children}</ul>,
    number: ({ children }) => <ol className="list-decimal ml-6 mb-4">{children}</ol>,
  },
  listItem: {
    bullet: ({ children }) => <li className="mb-1">{children}</li>,
    number: ({ children }) => <li className="mb-1">{children}</li>,
  },
};

const BlogPost = () => {
  const { slug } = useParams();
  const { islandConfig } = getRuntimeSiteContext();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    const fetchPost = async () => {
      const postQuery = `*[_type == "blogPost" && ${islandScopedDocumentFilter()} && slug.current == $slug][0]{
        _id,
        _updatedAt,
        title,
        "slug": slug.current,
        metaTitle,
        metaDescription,
        publishedAt,
        mainImage {
          asset->{ url },
          alt
        },
        author->{
          name,
          headshot{
            asset,
            alt
          }
        },
        body,
        excerpt,
        category->{ title, slug }
      }`;

      const result = await sanityFetch(postQuery, { slug });
      setPost(result);

      const categorySlug =
        result.category?.slug?.current || result.category?.slug || null;

      const relatedQuery = `*[
        _type == "blogPost" &&
        ${islandScopedDocumentFilter()} &&
        slug.current != $currentSlug &&
        (
          !defined($categorySlug) ||
          category->slug.current == $categorySlug
        )
      ] | order(coalesce(publishedAt, _createdAt) desc)[0...3] {
        _id,
        title,
        "slug": slug.current,
        excerpt,
        publishedAt,
        mainImage {
          asset->{ url },
          alt
        }
      }`;

      const allOthers = await sanityFetch(relatedQuery, {
        currentSlug: slug,
        categorySlug,
      });
      setRelatedPosts(allOthers.map(mapCardPost));
    };

    fetchPost();
  }, [slug]);

  if (!post) return <div className="text-center py-10">Loading...</div>;

  const pageTitle = post.metaTitle || `${post.title} | Alaka'i Aloha`;
  const pageDescription =
    post.metaDescription ||
    post.excerpt ||
    `${post.title} - Read more about ${islandConfig.shortTitle} travel insights and local expertise.`;
  const pageImage = post.mainImage?.asset?.url;
  const pageUrl = buildRuntimeCanonicalUrl(post.slug ? `/blog/${post.slug}` : '/blog');
  const siteName = "Alaka'i Aloha";
  const publishDate = post.publishedAt;
  const modifiedDate = post._updatedAt || null;
  const authorName = post.author?.name || null;
  const authorImageUrl = post.author?.headshot?.asset
    ? urlFor(post.author.headshot).width(96).height(96).fit('crop').auto('format').url()
    : null;

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: pageDescription,
    author: authorName
      ? {
          '@type': 'Person',
          name: authorName,
        }
      : {
          '@type': 'Organization',
          name: siteName,
        },
    publisher: {
      '@type': 'Organization',
      name: siteName,
    },
    ...(publishDate ? { datePublished: publishDate } : {}),
    ...(modifiedDate ? { dateModified: modifiedDate } : {}),
    ...(pageImage ? { image: pageImage } : {}),
    url: pageUrl,
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="article" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:site_name" content={siteName} />
        {pageImage && <meta property="og:image" content={pageImage} />}
        {publishDate && <meta property="article:published_time" content={publishDate} />}
        {modifiedDate && <meta property="article:modified_time" content={modifiedDate} />}
        {post.category?.title && <meta property="article:section" content={post.category.title} />}

        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content={pageTitle} />
        <meta property="twitter:description" content={pageDescription} />
        {pageImage && <meta property="twitter:image" content={pageImage} />}

        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      </Helmet>

      <BlogPostView
        post={{
          title: post.title,
          slug: post.slug,
          excerpt: post.excerpt,
          publishedAt: post.publishedAt,
          updatedAt: post._updatedAt,
          imageUrl: pageImage,
          imageAlt: post.mainImage?.alt || post.title,
          authorName,
          authorImageUrl,
          authorImageAlt: post.author?.headshot?.alt,
          category: post.category?.title && post.category?.slug?.current
            ? {
                title: post.category.title,
                slug: post.category.slug.current,
              }
            : undefined,
          bodyContent: <PortableText value={post.body} components={portableTextComponents} />,
        }}
        relatedPosts={relatedPosts}
        promoSlot={
          <PlanningPhasePromo
            pageType="blog"
            className="mt-16"
          />
        }
      />
    </>
  );
};

export default BlogPost;
